import React, { useState } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown
} from 'reactstrap';
import { Icon, TooltipComponent } from '../../../components/Component';
import TooltipCard from '../../../components/tooltip/TooltipCard';
import CardReorderModal from './CardReorderModal';

const IntranetSection = ({
  section,
  dragHandleProps,
  onEditSection,
  onDeleteSection,
  onCreateCard,
  onEditCard,
  onDeleteCard,
  onReorderCards,
  isAdminUser
}) => {
  const cards = section.cards || [];
  const [showReorderModal, setShowReorderModal] = useState(false);

  return (
    <>
      <Card className="rounded-3 border-0 shadow-sm mb-4">
        <CardHeader className="bg-transparent border-0 p-4">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex align-items-center">
              <div {...dragHandleProps} className="me-3 p-1 rounded" style={{ cursor: isAdminUser ? 'grab' : 'default' }}>
                <Icon name="move" style={{ fontSize: '1rem', color: '#9aa0ac' }}></Icon>
              </div>
              <h5 className="mb-0 fw-bold text-dark">{section.name}</h5>
              <span className="badge bg-secondary-dim text-dark rounded-pill ms-3 px-3">{cards.length}</span>
            </div>
            {isAdminUser && <div className="d-flex align-items-center gap-2">
              <Button color="primary" size="sm" className="rounded-pill px-3" onClick={() => onCreateCard(section.id)}>
                <Icon name="plus" className="me-1"></Icon>
                Add Card
              </Button>
              {cards.length > 1 && (
                <TooltipComponent
                  tag="button"
                  containerClassName="btn btn-sm btn-light rounded-circle p-2"
                  id={`reorder-cards-${section.id}`}
                  icon="sort"
                  direction="top"
                  text="Reorder Cards"
                  onClick={() => setShowReorderModal(true)}
                />
              )}
              <TooltipComponent
                tag="button"
                containerClassName="btn btn-sm btn-light rounded-circle p-2"
                id={`edit-section-${section.id}`}
                icon="edit-alt"
                direction="top"
                text="Edit Section"
                onClick={() => onEditSection(section)}
              />
              <TooltipComponent
                tag="button"
                containerClassName="btn btn-sm btn-light rounded-circle p-2"
                id={`delete-section-${section.id}`}
                icon="trash"
                direction="top"
                text="Delete Section"
                onClick={() => onDeleteSection(section)}
              />
            </div>}
          </div>
        </CardHeader>
        <CardBody className="p-4 pt-0">
          {cards.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <Icon name="list" style={{ fontSize: '3rem', color: '#e5e9f2' }}></Icon>
              </div>
              <p className="text-muted mb-4">{isAdminUser ? "No cards in this section yet" : "No cards in this section yet. Contact administrators to add cards."}</p>
              {isAdminUser && <Button color="primary" size="sm" className="rounded-pill px-4" onClick={() => onCreateCard(section.id)}>
                <Icon name="plus" className="me-1"></Icon>
                Add First Card
              </Button>}
            </div>
          ) : (
            <Droppable droppableId={`section-${section.id}`} direction="horizontal">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`card-grid ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                >
                  <Row className="g-3">
                    {cards.map((card, index) => (
                      <Col key={card.id} xl="3" lg="4" md="6" sm="12" style={{ minHeight: '135px' }}>
                        <IntranetCard
                          card={card}
                          onEdit={onEditCard}
                          onDelete={onDeleteCard}
                          isAdminUser={isAdminUser}
                        />
                      </Col>
                    ))}
                  </Row>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}
        </CardBody>
      </Card>

      {showReorderModal && (
        <CardReorderModal
          show={showReorderModal}
          onHide={() => setShowReorderModal(false)}
          section={section}
          cards={cards}
          onReorder={onReorderCards}
        />
      )}
    </>
  );
};

const IntranetCard = ({ card, onEdit, onDelete, isAdminUser }) => {
  const handleCardClick = (e) => {
    // Prevent navigation when clicking action buttons
    if (e.target.closest('.card-actions')) {
      return;
    }

    // Open the link if it exists
    if (card.link) {
      // Check if it's an external link (starts with http)
      if (card.link.startsWith('http://') || card.link.startsWith('https://')) {
        window.open(card.link, '_blank', 'noopener noreferrer');
      } else {
        // Internal link - navigate to it
        window.location.href = card.link;
      }
    }
  };

  return (
    <Card
      className="intranet-card rounded-3 shadow-sm"
      style={{
        cursor: card.link ? 'pointer' : 'default',
        backgroundColor: '#fafbfc',
        border: '1px solid #e3e7ed'
      }}
      onClick={handleCardClick}
    >
      <CardBody className="p-3 d-flex flex-column justify-content-between">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex align-items-center flex-grow-1">
            <h6 className="card-title mb-0 mt-2 ms-2 fw-semibold text-dark">{card.title}</h6>
          </div>
          {isAdminUser && <div className="card-actions">
            <UncontrolledDropdown>
              <DropdownToggle
                tag="button"
                className="btn btn-sm btn-light rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: '28px', height: '28px' }}
                onClick={(e) => e.stopPropagation()}
              >
                <Icon name="more-h" style={{ fontSize: '0.75rem' }}></Icon>
              </DropdownToggle>
              <DropdownMenu end flip className="rounded-3 border-0 shadow">
                <ul className="link-list-opt no-bdr p-1">
                  <li>
                    <DropdownItem
                      tag="button"
                      className="rounded-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(card);
                      }}
                    >
                      <Icon name="edit" className="me-2"></Icon>
                      Edit
                    </DropdownItem>
                  </li>
                  <li>
                    <DropdownItem
                      tag="button"
                      className="text-danger rounded-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(card);
                      }}
                    >
                      <Icon name="trash" className="me-2"></Icon>
                      Delete
                    </DropdownItem>
                  </li>
                </ul>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>}
        </div>
        {card.link && (
          <div className="mt-3 pt-2 ms-2">
            <TooltipCard 
              icon="external"
              iconClassName="text-primary me-1"
              openDelay={200}
            >
              <div className="rounded" style={{ maxWidth: '250px', wordBreak: 'break-all', fontSize: '0.8rem' }}>
                <div className="text-muted">{card.link}</div>
              </div>
            </TooltipCard>
            <small className="text-primary ms-1" style={{ fontSize: '0.8rem' }}>
              {card.link.startsWith('http') ? 'External Link' : 'Internal Link'}
            </small>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default IntranetSection;
